import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, CircularProgress, Divider, TextField,
  InputAdornment, useMediaQuery, useTheme,
} from '@mui/material';
import { aprovadorApi, AprovadorError, Review, Saldo, brl } from '@/services/aprovador';

interface Props {
  token: string;
  open: boolean;
  saldoSicoob: Saldo | undefined;
  saldoEfi: Saldo | null;
  onClose: () => void;
  onDone: (mensagem: string) => void;
  onSessionLost: () => void;
}

const Linha: React.FC<{ rotulo: string; valor: React.ReactNode; forte?: boolean }> = ({ rotulo, valor, forte }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
    <Typography variant="body2" fontWeight={forte ? 700 : 500} textAlign="right" sx={{ wordBreak: 'break-word' }}>{valor}</Typography>
  </Box>
);

/** Converte "1.234,56" ou "1234.56" em número. Retorna NaN se inválido. */
function parseValor(texto: string): number {
  const limpo = texto.trim().replace(/\s/g, '');
  if (!limpo) return NaN;
  const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  return Number(normalizado);
}

/**
 * Transferência do saldo Sicoob para a conta Efí da própria empresa.
 * Passo 1: valor → "Conferir" (banco resolve o titular da chave).
 * Passo 2: recebedor + valor → "Confirmar transferência" ou "Cancelar".
 */
const TransferirEfiDialog: React.FC<Props> = ({ token, open, saldoSicoob, saldoEfi, onClose, onDone, onSessionLost }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [passo, setPasso] = useState<1 | 2>(1);
  const [valorTexto, setValorTexto] = useState('');
  const [review, setReview] = useState<Review | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setPasso(1); setValorTexto(''); setReview(null); setError(null); setBusy(false); };

  const fechar = () => {
    if (busy) return;
    if (review) {
      // Fechar com a transferência conferida = desistir; cancela no servidor
      aprovadorApi.transferirEfiCancelar(token, review.id).catch(() => {});
    }
    reset();
    onClose();
  };

  const tratarErro = (e: unknown) => {
    const err = e as AprovadorError;
    if (err.status === 401) { onSessionLost(); return; }
    setError(err.message);
  };

  const conferir = async () => {
    if (busy) return;
    const valor = parseValor(valorTexto);
    if (!Number.isFinite(valor) || valor <= 0) { setError('Informe um valor maior que zero.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await aprovadorApi.transferirEfiConferir(token, valor);
      setReview(r);
      setPasso(2);
    } catch (e) {
      tratarErro(e);
    } finally {
      setBusy(false);
    }
  };

  const confirmar = async () => {
    if (busy || !review) return;
    setBusy(true); setError(null);
    try {
      const r = await aprovadorApi.confirmar(token, review.id);
      if (r.ok) {
        reset();
        onDone(`Transferência de ${brl(review.valor)} para a conta Efí concluída.`);
      } else {
        setError(r.error || 'A transferência não foi concluída. Confira no painel.');
      }
    } catch (e) {
      tratarErro(e);
    } finally {
      setBusy(false);
    }
  };

  const cancelar = async () => {
    if (busy || !review) return;
    setBusy(true); setError(null);
    try {
      await aprovadorApi.transferirEfiCancelar(token, review.id);
      setPasso(1); setReview(null);
    } catch (e) {
      tratarErro(e);
    } finally {
      setBusy(false);
    }
  };

  const valorNum = parseValor(valorTexto);
  const semSaldo = Number.isFinite(valorNum) && saldoSicoob?.disponivel != null && valorNum > saldoSicoob.disponivel;

  return (
    <Dialog open={open} onClose={fechar} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {passo === 1 ? 'Transferir para a conta Efí' : 'Confira a transferência'}
      </DialogTitle>
      <DialogContent dividers>
        {passo === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Sai do saldo do Sicoob, por Pix, para a conta Efí desta empresa. O destino é fixo.
            </Typography>
            <TextField
              label="Valor"
              value={valorTexto}
              onChange={(e) => setValorTexto(e.target.value)}
              disabled={busy}
              fullWidth
              autoFocus
              inputMode="decimal"
              placeholder="0,00"
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            />
            <Divider sx={{ my: 1.5 }} />
            <Linha rotulo="Saldo Sicoob" valor={saldoSicoob?.disponivel != null ? brl(saldoSicoob.disponivel) : 'indisponível'} />
            <Linha rotulo="Saldo Efí" valor={saldoEfi?.disponivel != null ? brl(saldoEfi.disponivel) : (saldoEfi?.error || 'indisponível')} />
            {semSaldo && <Alert severity="error" sx={{ mt: 1.5 }}>Saldo Sicoob insuficiente para este valor.</Alert>}
          </>
        )}

        {passo === 2 && review && (
          <>
            <Typography variant="overline" color="text.secondary">Destino (titular confirmado pelo banco)</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>{review.recebedor.nome || '—'}</Typography>
            {review.recebedor.documento && <Typography variant="body2" color="text.secondary">CPF/CNPJ {review.recebedor.documento}</Typography>}
            <Divider sx={{ my: 1.5 }} />
            <Linha rotulo="Valor" valor={brl(review.valor)} forte />
            <Linha rotulo="Saldo Sicoob" valor={saldoSicoob?.disponivel != null ? brl(saldoSicoob.disponivel) : 'indisponível'} />
            <Linha rotulo="Saldo Efí" valor={saldoEfi?.disponivel != null ? brl(saldoEfi.disponivel) : (saldoEfi?.error || 'indisponível')} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Ao confirmar, o Pix sai na hora da conta Sicoob. <b>Não dá para desfazer.</b>
            </Typography>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {passo === 1 && (
          <>
            <Button onClick={fechar} disabled={busy}>Cancelar</Button>
            <Button variant="contained" disabled={busy || !valorTexto.trim() || semSaldo} onClick={conferir} sx={{ flex: 1 }}>
              {busy ? <CircularProgress size={22} color="inherit" /> : 'Conferir'}
            </Button>
          </>
        )}
        {passo === 2 && (
          <>
            <Button onClick={cancelar} disabled={busy}>Cancelar</Button>
            <Button variant="contained" color="success" disabled={busy || !review} onClick={confirmar} sx={{ flex: 1 }}>
              {busy ? <CircularProgress size={22} color="inherit" /> : 'Confirmar transferência'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferirEfiDialog;
